package com.devi3east.bashbuddy.prizeList.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.features.common.util.DateTimeInput
import com.devi3east.bashbuddy.prizeList.domain.models.Prize
import com.devi3east.bashbuddy.prizeList.domain.models.PrizeType
import com.devi3east.bashbuddy.prizeList.requests.UpdatePrizeRequest
import kotlinx.datetime.Instant

@Composable
fun UpdatePrizeDialog(
    initialInstant: Instant,
    prize: Prize,
    isEditing: Boolean,
    onDismiss: () -> Unit,
    onDelete: (String) -> Unit,
    onSubmit: (UpdatePrizeRequest) -> Unit
) {

    // ✅ CRITICAL: use TextFieldValue with selection preservation
    var title by remember {
        mutableStateOf(
            TextFieldValue(
                text = prize.title ?: "",
                selection = TextRange((prize.title ?: "").length)
            )
        )
    }

    var serialNumber by remember {
        mutableStateOf(
            TextFieldValue(
                text = prize.serialNumber ?: "",
                selection = TextRange((prize.serialNumber ?: "").length)
            )
        )
    }

    var winningNumber by remember {
        mutableStateOf(
            TextFieldValue(
                text = prize.winningNumber ?: "",
                selection = TextRange((prize.winningNumber ?: "").length)
            )
        )
    }

    val prizeType = prize.prizeType

    var timeSlotInstant by remember {
        mutableStateOf(prize.timeSlotInstant ?: initialInstant)
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = if (isEditing) {
                    if (prizeType == PrizeType.MAIN_EVENT) "Update Prize" else "Update Side Raffle"
                } else {
                    if (prizeType == PrizeType.MAIN_EVENT) "Add Prize" else "Add Side Raffle"
                },
                style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.SemiBold),
                modifier = Modifier.fillMaxWidth(),
                textAlign = androidx.compose.ui.text.style.TextAlign.Center
            )
        },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(10.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {

                if (prizeType == PrizeType.MAIN_EVENT) {

                    DateTimeInput(
                        instant = timeSlotInstant,
                        onDateTimeChange = { timeSlotInstant = it }
                    )

                    OutlinedTextField(
                        value = title,
                        onValueChange = { newValue ->
                            // ✅ CRITICAL: preserve selection
                            title = newValue.copy(
                                selection = TextRange(newValue.text.length)
                            )
                        },
                        label = { Text("Prize") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Text,
                            imeAction = ImeAction.Next,
                            autoCorrectEnabled = false // 🔥 REQUIRED
                        )
                    )

                    if (isEditing) {
                        OutlinedTextField(
                            value = winningNumber,
                            onValueChange = { newValue ->
                                winningNumber = newValue.copy(
                                    selection = TextRange(newValue.text.length)
                                )
                            },
                            label = { Text("Winning Number") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            shape = RoundedCornerShape(12.dp),
                            keyboardOptions = KeyboardOptions(
                                keyboardType = KeyboardType.Number,
                                imeAction = ImeAction.Done,
                                autoCorrectEnabled = false
                            )
                        )
                    }
                }

                if (prizeType == PrizeType.SIDE_RAFFLE) {

                    OutlinedTextField(
                        value = serialNumber,
                        onValueChange = { newValue ->
                            serialNumber = newValue.copy(
                                selection = TextRange(newValue.text.length)
                            )
                        },
                        label = { Text("Serial Number") },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !isEditing,
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Text,
                            imeAction = ImeAction.Next,
                            autoCorrectEnabled = false
                        )
                    )

                    OutlinedTextField(
                        value = title,
                        onValueChange = { newValue ->
                            title = newValue.copy(
                                selection = TextRange(newValue.text.length)
                            )
                        },
                        label = { Text("Prize") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Text,
                            imeAction = ImeAction.Next,
                            autoCorrectEnabled = false
                        )
                    )

                    if (isEditing) {
                        OutlinedTextField(
                            value = winningNumber,
                            onValueChange = { newValue ->
                                winningNumber = newValue.copy(
                                    selection = TextRange(newValue.text.length)
                                )
                            },
                            label = { Text("Winning Number") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            shape = RoundedCornerShape(12.dp),
                            keyboardOptions = KeyboardOptions(
                                keyboardType = KeyboardType.Number,
                                imeAction = ImeAction.Done,
                                autoCorrectEnabled = false
                            )
                        )
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    onSubmit(
                        UpdatePrizeRequest(
                            title = title.text,
                            serialNumber = serialNumber.text,
                            timeSlot = timeSlotInstant,
                            winningNumber = winningNumber.text,
                            prizeType = prizeType
                        )
                    )
                    onDismiss()
                },
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = MaterialTheme.colorScheme.onPrimary
                )
            ) {
                Text("Save", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold))
            }
        },
        dismissButton = {
            TextButton(
                onClick = {
                    onDelete(prize._id)
                    onDismiss()
                }
            ) {
                Text(
                    "Cancel",
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold),
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    )
}